import { useState, useCallback, useMemo } from 'react';
import './SitemapFilter.css';

let nextId = 1;

const MATCH_TYPES = {
  contains: { label: 'contains', placeholder: 'e.g. blog' },
  starts_with: { label: 'starts with', placeholder: 'e.g. /product/' },
  ends_with: { label: 'ends with', placeholder: 'e.g. .pdf' },
  equals: { label: 'is exactly', placeholder: 'e.g. https://example.com/pricing' },
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const makeFilter = () => ({ id: nextId++, matchType: 'contains', value: '', pattern: '' });

const toRegexPattern = (row, isAdvanced) => {
  if (isAdvanced) return row.pattern;
  const escaped = escapeRegex(row.value);
  switch (row.matchType) {
    case 'starts_with':
      return `^${escaped}`;
    case 'ends_with':
      return `${escaped}$`;
    case 'equals':
      return `^${escaped}$`;
    case 'contains':
    default:
      return escaped;
  }
};

const SitemapFilter = () => {
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [filters, setFilters] = useState([makeFilter()]);
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { xml, totalUrls, filteredUrls, urls }

  const addFilter = () => setFilters((f) => [...f, makeFilter()]);
  const removeFilter = (id) => setFilters((f) => f.filter((row) => row.id !== id));
  const updateFilter = (id, patch) =>
    setFilters((f) => f.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const activeFilters = filters.filter((row) => (isAdvanced ? row.pattern.trim() : row.value.trim()));

  const invalidPatterns = useMemo(() => {
    if (!isAdvanced) return {};
    const invalid = {};
    filters.forEach((row) => {
      if (!row.pattern) return;
      try {
        new RegExp(row.pattern);
      } catch (e) {
        invalid[row.id] = e.message;
      }
    });
    return invalid;
  }, [filters, isAdvanced]);

  const buildEndpointUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set('sitemap_url', sitemapUrl);
    params.set('combinator', 'or');
    filters
      .filter((row) => (isAdvanced ? row.pattern.trim() : row.value.trim()))
      .forEach((row) => params.append('filter', `include:${toRegexPattern(row, isAdvanced)}`));
    return `/filtered-sitemap?${params.toString()}`;
  }, [sitemapUrl, filters, isAdvanced]);

  const endpointUrl = useMemo(() => buildEndpointUrl(), [buildEndpointUrl]);
  const hasInvalid = Object.keys(invalidPatterns).length > 0;

  const runFilter = async () => {
    setError('');
    setResult(null);

    if (!sitemapUrl.trim()) {
      setError('Please enter a sitemap URL.');
      return;
    }
    if (hasInvalid) {
      setError('Fix invalid regex patterns before running.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(endpointUrl);
      const text = await res.text();
      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          message = JSON.parse(text).error || message;
        } catch (_) {}
        setError(message);
        return;
      }
      const totalUrls = res.headers.get('X-Total-URLs');
      const filteredUrls = res.headers.get('X-Filtered-URLs');
      const urls = Array.from(text.matchAll(/<loc>(.*?)<\/loc>/g)).map((m) => m[1]);
      setResult({ xml: text, totalUrls, filteredUrls, urls });
    } catch (e) {
      setError('Network error while fetching filtered sitemap.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyEndpoint = () => {
    const absolute = `${window.location.origin}${endpointUrl}`;
    navigator.clipboard.writeText(absolute);
  };

  const downloadXml = () => {
    if (!result) return;
    const blob = new Blob([result.xml], { type: 'application/xml' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'filtered-sitemap.xml';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="sf-page">
      <div className="sf-container">
        <h1 className="sf-title">Sitemap Filter Builder</h1>
        <p className="sf-subtitle">
          Choose which pages to keep from a sitemap, and preview the result live.
          Nothing is uploaded — the result always reflects the current state of the source sitemap.
        </p>

        <div className="sf-card sf-fade-in">
          <label className="sf-label" htmlFor="sitemap-url">Sitemap URL</label>
          <input
            id="sitemap-url"
            type="text"
            className="sf-input"
            placeholder="https://example.com/sitemap.xml"
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
          />

          <div className="sf-filters-header">
            <span className="sf-section-label">Keep pages that match any of these rules</span>
            <label className="sf-advanced-toggle">
              <input
                type="checkbox"
                checked={isAdvanced}
                onChange={(e) => setIsAdvanced(e.target.checked)}
              />
              Advanced (regex)
            </label>
          </div>

          <div className="sf-filter-list">
            {filters.map((row) => (
              <div key={row.id} className={`sf-filter-row sf-fade-in ${isAdvanced ? 'sf-filter-row-advanced' : ''}`}>
                {isAdvanced ? (
                  <div className="sf-pattern-wrap">
                    <input
                      type="text"
                      className={`sf-input sf-mono ${invalidPatterns[row.id] ? 'sf-input-invalid' : ''}`}
                      placeholder="e.g. /blog/.*"
                      value={row.pattern}
                      onChange={(e) => updateFilter(row.id, { pattern: e.target.value })}
                    />
                    {invalidPatterns[row.id] && (
                      <p className="sf-error-inline">{invalidPatterns[row.id]}</p>
                    )}
                  </div>
                ) : (
                  <>
                    <span className="sf-where-label">Keep pages where the URL</span>
                    <select
                      className="sf-select sf-matchtype-select"
                      value={row.matchType}
                      onChange={(e) => updateFilter(row.id, { matchType: e.target.value })}
                    >
                      {Object.entries(MATCH_TYPES).map(([key, cfg]) => (
                        <option key={key} value={key}>{cfg.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="sf-input sf-value-input"
                      placeholder={MATCH_TYPES[row.matchType].placeholder}
                      value={row.value}
                      onChange={(e) => updateFilter(row.id, { value: e.target.value })}
                    />
                  </>
                )}

                <button
                  type="button"
                  className="sf-remove-btn"
                  onClick={() => removeFilter(row.id)}
                  disabled={filters.length === 1}
                  title="Remove rule"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button type="button" className="sf-add-link" onClick={addFilter}>
            + Add another rule
          </button>

          <div className="sf-actions">
            <button
              type="button"
              className="sf-btn sf-btn-primary"
              onClick={runFilter}
              disabled={isLoading || hasInvalid}
            >
              {isLoading && <span className="sf-spinner" />}
              {isLoading ? 'Filtering…' : 'Preview filtered sitemap'}
            </button>
            <button
              type="button"
              className="sf-btn sf-btn-outline"
              onClick={copyEndpoint}
              disabled={!sitemapUrl.trim()}
            >
              Copy endpoint URL
            </button>
          </div>

          {sitemapUrl.trim() && (
            <div className="sf-endpoint-preview">
              <span className="sf-endpoint-method">GET</span>
              <span className="sf-endpoint-url">{endpointUrl}</span>
            </div>
          )}

          {error && <p className="sf-error">{error}</p>}
        </div>

        {result && (
          <div className="sf-card sf-fade-in">
            <div className="sf-result-header">
              <h2 className="sf-result-title">
                Filtered result — {result.filteredUrls ?? result.urls.length} of{' '}
                {result.totalUrls ?? '?'} pages kept
                {activeFilters.length > 1 && (
                  <span className="sf-result-meta">
                    {' '}
                    ({activeFilters.length} rules, matching any)
                  </span>
                )}
              </h2>
              <button type="button" className="sf-btn sf-btn-ghost" onClick={downloadXml}>
                Download XML
              </button>
            </div>
            <div className="sf-url-list">
              {result.urls.length === 0 && (
                <p className="sf-empty">No pages matched — sitemap would be empty.</p>
              )}
              {result.urls.map((u, i) => (
                <div key={i} className="sf-url-row">
                  {u}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SitemapFilter;
